import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Users,
  Mail,
  Loader2,
  Trash2,
  Check,
  Clock,
  Crown,
  UserPlus,
  ShieldCheck,
  Sparkles,
  LogOut,
  Send,
  WifiOff,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useT } from '../lib/i18n';
import { teamApi } from '../lib/teamApi';

const fmt = (str, vars) =>
  Object.entries(vars || {}).reduce(
    (s, [k, v]) => s.replace(new RegExp(`\\{${k}\\}`, 'g'), v),
    str
  );

// Cache key for offline support — last successful /team/info response per user.
const teamCacheKey = (uid) => `sp.team.info.cache:${uid || 'anon'}`;

function readTeamCache(uid) {
  try {
    const raw = localStorage.getItem(teamCacheKey(uid));
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function writeTeamCache(uid, data) {
  try { localStorage.setItem(teamCacheKey(uid), JSON.stringify(data)); }
  catch { /* quota / disabled — ignore */ }
}

export default function Team() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, lang } = useT();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isOffline, setIsOffline] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [resendingId, setResendingId] = useState(null);
  const [leaving, setLeaving] = useState(false);

  const load = async () => {
    setLoading(true);
    // 🌐 Offline-first: si pas de réseau, charger directement le cache et
    // afficher un bandeau d'information. Aucun toast d'erreur — l'app DOIT
    // rester utilisable hors-ligne (exigence Apple Review + chantiers).
    if (!navigator.onLine) {
      const cached = readTeamCache(user?.id);
      if (cached) setInfo(cached);
      setIsOffline(true);
      setLoading(false);
      return;
    }
    try {
      const data = await teamApi.info();
      setInfo(data);
      setIsOffline(false);
      writeTeamCache(user?.id, data);
    } catch (e) {
      // Fallback: utiliser le cache local plutôt qu'afficher un toast d'erreur.
      const cached = readTeamCache(user?.id);
      if (cached) {
        setInfo(cached);
        setIsOffline(true);
      } else if (e?.status === 402) {
        // 402 = paywall. Le fallback JSX 'team-empty-state' affichera déjà
        // un message clair + CTA "Voir les plans". Pas de toast redondant.
      } else {
        toast.error(e.message || t('team.load_error'));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) load();
  }, [user]);

  // Bilingual offline message — used by every action handler below.
  const offlineMsg = () => (lang === 'fr'
    ? 'Hors ligne — cette action nécessite une connexion internet.'
    : 'Offline — this action requires an internet connection.');

  const handleInvite = async (e) => {
    e?.preventDefault?.();
    const email = inviteEmail.trim();
    if (!email) return;
    if (!navigator.onLine) { toast.error(offlineMsg()); return; }
    setInviting(true);
    try {
      const res = await teamApi.invite(email);
      if (res?.email_sent) {
        toast.success(fmt(t('team.invite_sent'), { email }));
      } else {
        toast.warning(fmt(t('team.invite_no_email'), { email }), { duration: 7000 });
      }
      setInviteEmail('');
      await load();
    } catch (e) {
      toast.error(e.message || t('team.invite_error'));
    } finally {
      setInviting(false);
    }
  };

  const handleResend = async (memberId, label) => {
    if (!navigator.onLine) { toast.error(offlineMsg()); return; }
    setResendingId(memberId);
    try {
      const res = await teamApi.resend(memberId);
      if (res?.email_sent) {
        toast.success(fmt(t('team.resend_sent'), { email: label }));
      } else {
        toast.error(t('team.resend_failed'));
      }
    } catch (e) {
      toast.error(e.message || t('team.resend_error'));
    } finally {
      setResendingId(null);
    }
  };

  const handleRemove = async (memberId, label) => {
    if (!window.confirm(fmt(t('team.remove_confirm'), { name: label }))) return;
    if (!navigator.onLine) { toast.error(offlineMsg()); return; }
    setRemovingId(memberId);
    try {
      await teamApi.remove(memberId);
      toast.success(t('team.removed'));
      await load();
    } catch (e) {
      toast.error(e.message || t('team.generic_error'));
    } finally {
      setRemovingId(null);
    }
  };

  const handleLeave = async () => {
    if (!window.confirm(t('team.leave_confirm'))) return;
    if (!navigator.onLine) { toast.error(offlineMsg()); return; }
    setLeaving(true);
    try {
      await teamApi.leave();
      toast.success(t('team.leave_success'));
      setTimeout(() => window.location.assign('/'), 800);
    } catch (e) {
      toast.error(e.message || t('team.generic_error'));
      setLeaving(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FAF7F2' }}>
      <header
        className="px-4 sm:px-8 py-4 flex items-center justify-between border-b sticky top-0 z-10 bg-[#FAF7F2] pt-safe"
        style={{ borderColor: '#E5DCC9' }}
      >
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-sm font-medium"
          style={{ color: '#7A1F2B' }}
          data-testid="team-back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> {t('team.back')}
        </button>
        <div className="font-bold text-sm tracking-widest" style={{ color: '#7A1F2B' }}>
          {t('team.title')}
        </div>
        <div className="w-16" />
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-8 py-8 space-y-6">
        {isOffline && !loading && (
          <div
            className="p-3 rounded-xl flex items-center gap-2 text-sm"
            style={{ backgroundColor: '#FEF3C7', border: '1px solid #FCD34D', color: '#92400E' }}
            data-testid="team-offline-banner"
          >
            <WifiOff className="w-4 h-4 flex-shrink-0" />
            <span>{t('team.offline_banner') || (t('common.offline_cached') || 'Hors ligne — données en cache. Les modifications nécessitent une connexion.')}</span>
          </div>
        )}

        {loading && (
          <div className="flex items-center justify-center py-20" data-testid="team-loading">
            <Loader2 className="w-6 h-6 animate-spin" style={{ color: '#7A1F2B' }} />
          </div>
        )}

        {!loading && info?.role === 'member' && (
          <MemberView info={info} onLeave={handleLeave} leaving={leaving} t={t} />
        )}

        {!loading && info?.role === 'owner' && (
          <OwnerView
            info={info}
            inviteEmail={inviteEmail}
            setInviteEmail={setInviteEmail}
            onInvite={handleInvite}
            inviting={inviting}
            onRemove={handleRemove}
            removingId={removingId}
            onResend={handleResend}
            resendingId={resendingId}
            onUpgrade={() => navigate('/pricing')}
            t={t}
          />
        )}

        {/* 🆘 Fallback : aucun rôle reconnu ET pas en loading ET pas offline.
            Couvre le cas où l'API a échoué (402 sans cache, 500, etc.) OU
            où le user n'a aucun enregistrement dans team_members (nouveau
            compte). Évite l'écran blanc qui semblait être un crash. */}
        {!loading && !isOffline && (!info || (!info.role)) && (
          <div
            className="rounded-2xl p-8 text-center"
            style={{ backgroundColor: '#FAF7F2', border: '1px solid #E5DCC9' }}
            data-testid="team-empty-state"
          >
            <Users className="w-10 h-10 mx-auto mb-3" style={{ color: '#C9A961' }} />
            <h2 className="font-bold text-lg mb-2" style={{ color: '#5A1620' }}>
              {lang === 'fr' ? 'Aucune équipe configurée' : 'No team configured'}
            </h2>
            <p className="text-sm mb-5" style={{ color: '#4A5568' }}>
              {lang === 'fr'
                ? "Tu n'as pas encore d'équipe ou ton abonnement actuel ne permet pas la fonction Équipe. Souscris à un plan Équipe ou Pro pour inviter des collaborateurs."
                : "You don't have a team yet, or your current subscription doesn't include Team features. Subscribe to a Team or Pro plan to invite collaborators."}
            </p>
            <button
              onClick={() => navigate('/pricing')}
              className="px-5 py-2.5 rounded-xl font-semibold text-white"
              style={{ backgroundColor: '#7A1F2B' }}
              data-testid="team-empty-upgrade-btn"
            >
              {lang === 'fr' ? 'Voir les plans' : 'View plans'}
            </button>
          </div>
        )}
      </main>
    </div>
  );
}

function MemberView({ info, onLeave, leaving, t }) {
  return (
    <div data-testid="team-member-view">
      <div
        className="rounded-2xl p-6 sm:p-8 text-white"
        style={{ background: 'linear-gradient(135deg,#7A1F2B 0%,#5A1620 100%)' }}
      >
        <div className="flex items-center gap-2 text-xs font-bold tracking-widest mb-3" style={{ color: '#C9A961' }}>
          <ShieldCheck className="w-3.5 h-3.5" /> {t('team.member_badge')}
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold mb-2" style={{ letterSpacing: '-0.5px' }}>
          {info.owner_company || 'SoumissionPro'}
        </h1>
        <p className="text-sm opacity-80 mb-6">
          {t('team.member_intro')}
        </p>
        <div className="rounded-xl p-4 bg-[#FFFFFF15] border border-[#FFFFFF22]">
          <div className="text-xs uppercase tracking-widest mb-1" style={{ color: '#C9A961' }}>
            {t('team.owner_label')}
          </div>
          <div className="text-base font-semibold">{info.owner_email}</div>
        </div>
      </div>

      <button
        onClick={onLeave}
        disabled={leaving}
        className="mt-6 w-full py-3.5 rounded-xl font-semibold inline-flex items-center justify-center gap-2 border-2 transition disabled:opacity-50"
        style={{ borderColor: '#7A1F2B', color: '#7A1F2B', backgroundColor: '#FFFFFF' }}
        data-testid="team-leave-btn"
      >
        {leaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogOut className="w-4 h-4" />}
        {t('team.leave_btn')}
      </button>
    </div>
  );
}

function OwnerView({ info, inviteEmail, setInviteEmail, onInvite, inviting, onRemove, removingId, onResend, resendingId, onUpgrade, t }) {
  const seatsMax = info.seats_max || 1;
  const seatsUsed = info.seats_used || 1;
  const canInvite = seatsMax > 1 && seatsUsed < seatsMax;
  const seatsLeft = Math.max(0, seatsMax - seatsUsed);

  return (
    <>
      {/* Header card */}
      <div
        className="rounded-2xl p-6 sm:p-8 text-white"
        style={{ background: 'linear-gradient(135deg,#7A1F2B 0%,#5A1620 100%)' }}
        data-testid="team-owner-header"
      >
        <div className="flex items-center gap-2 text-xs font-bold tracking-widest mb-3" style={{ color: '#C9A961' }}>
          <Crown className="w-3.5 h-3.5" /> {t('team.owner_badge')}
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold mb-2" style={{ letterSpacing: '-0.5px' }}>
          {info.owner_company || t('team.owner_title')}
        </h1>
        <p className="text-sm opacity-80 mb-6">
          {t('team.owner_intro')}
        </p>
        <div className="flex items-end justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest mb-1" style={{ color: '#C9A961' }}>
              {t('team.seats_used')}
            </div>
            <div className="text-4xl font-bold">
              {seatsUsed}
              <span className="text-2xl opacity-60"> / {seatsMax}</span>
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs opacity-70">
              {seatsLeft} {seatsLeft > 1 ? t('team.seats_available_many') : t('team.seats_available_one')}
            </div>
          </div>
        </div>
        <div className="mt-3 h-2 rounded-full bg-[#FFFFFF22] overflow-hidden">
          <div
            className="h-full rounded-full transition-all"
            style={{
              width: `${Math.min(100, (seatsUsed / Math.max(1, seatsMax)) * 100)}%`,
              backgroundColor: '#C9A961',
            }}
          />
        </div>
      </div>

      {/* Plan upgrade banner — solo plan can't invite */}
      {seatsMax <= 1 && (
        <div
          className="rounded-2xl p-6 border-2 bg-white"
          style={{ borderColor: '#C9A961' }}
          data-testid="team-upgrade-banner"
        >
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#FAF7F2' }}>
              <Sparkles className="w-5 h-5" style={{ color: '#C9A961' }} />
            </div>
            <div className="flex-1">
              <h3 className="text-base font-bold mb-1" style={{ color: '#5A1620' }}>
                {t('team.upgrade_title')}
              </h3>
              <p className="text-sm mb-4" style={{ color: '#4A5568' }} dangerouslySetInnerHTML={{ __html: t('team.upgrade_desc_html') }} />
              <button
                onClick={onUpgrade}
                className="px-5 py-2.5 rounded-xl font-semibold text-white text-sm"
                style={{ backgroundColor: '#7A1F2B' }}
                data-testid="team-upgrade-btn"
              >
                {t('team.upgrade_btn')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invite form */}
      {seatsMax > 1 && (
        <div className="rounded-2xl p-6 bg-white border" style={{ borderColor: '#E5DCC9' }}>
          <h3 className="text-base font-bold mb-1 inline-flex items-center gap-2" style={{ color: '#5A1620' }}>
            <UserPlus className="w-4 h-4" style={{ color: '#7A1F2B' }} /> {t('team.invite_title')}
          </h3>
          <p className="text-xs mb-4" style={{ color: '#6B7280' }}>
            {t('team.invite_desc')}
          </p>
          <form onSubmit={onInvite} className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#9CA3AF' }} />
              <input
                type="email"
                required
                placeholder={t('team.invite_placeholder')}
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                disabled={!canInvite || inviting}
                className="w-full pl-10 pr-3 py-3 rounded-xl border-2 text-sm focus:outline-none focus:border-[#7A1F2B] transition disabled:opacity-50"
                style={{ borderColor: '#E5DCC9', color: '#5A1620' }}
                data-testid="team-invite-email-input"
              />
            </div>
            <button
              type="submit"
              disabled={!canInvite || inviting}
              className="px-6 py-3 rounded-xl font-semibold text-white text-sm inline-flex items-center justify-center gap-2 transition disabled:opacity-50"
              style={{ backgroundColor: '#7A1F2B' }}
              data-testid="team-invite-submit-btn"
            >
              {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
              {t('team.invite_btn')}
            </button>
          </form>
          {!canInvite && seatsMax > 1 && (
            <p className="mt-3 text-xs font-medium" style={{ color: '#B45309' }}>
              ⚠️ {fmt(t('team.limit_warning'), { n: seatsMax })}
            </p>
          )}
        </div>
      )}

      {/* Members list */}
      <div className="rounded-2xl p-6 bg-white border" style={{ borderColor: '#E5DCC9' }} data-testid="team-members-list">
        <h3 className="text-base font-bold mb-4 inline-flex items-center gap-2" style={{ color: '#5A1620' }}>
          <Users className="w-4 h-4" style={{ color: '#7A1F2B' }} /> {t('team.members_title')} ({1 + (info.members?.length || 0)})
        </h3>

        <div className="space-y-2">
          {/* Owner row */}
          <div className="flex items-center gap-3 p-3 rounded-xl" style={{ backgroundColor: '#FAF7F2' }} data-testid="team-member-owner">
            <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold" style={{ backgroundColor: '#7A1F2B' }}>
              <Crown className="w-5 h-5" style={{ color: '#C9A961' }} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate" style={{ color: '#5A1620' }}>
                {info.owner_email}
              </div>
              <div className="text-xs" style={{ color: '#7A1F2B' }}>
                {t('team.owner_you')}
              </div>
            </div>
          </div>

          {/* Member rows */}
          {(info.members || []).map((m) => (
            <div
              key={m.id}
              className="flex items-center gap-3 p-3 rounded-xl border"
              style={{ borderColor: '#F3EBDB' }}
              data-testid={`team-member-${m.id}`}
            >
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: '#FAF7F2' }}>
                {m.status === 'pending' ? (
                  <Clock className="w-5 h-5" style={{ color: '#C9A961' }} />
                ) : (
                  <Check className="w-5 h-5" style={{ color: '#16A34A' }} />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate" style={{ color: '#5A1620' }}>
                  {m.member_email}
                </div>
                <div className="text-xs" style={{ color: m.status === 'pending' ? '#B45309' : '#16A34A' }}>
                  {m.status === 'pending' ? t('team.status_pending') : t('team.status_active')}
                </div>
              </div>
              {m.status === 'pending' && (
                <button
                  onClick={() => onResend(m.id, m.member_email)}
                  disabled={resendingId === m.id}
                  className="px-2.5 py-1.5 rounded-lg text-xs font-semibold inline-flex items-center gap-1.5 transition disabled:opacity-50"
                  style={{ backgroundColor: '#FAF7F2', color: '#7A1F2B', border: '1px solid #E5DCC9' }}
                  aria-label={t('team.resend')}
                  data-testid={`team-resend-${m.id}-btn`}
                >
                  {resendingId === m.id ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Send className="w-3.5 h-3.5" />
                  )}
                  <span className="hidden sm:inline">{t('team.resend')}</span>
                </button>
              )}
              <button
                onClick={() => onRemove(m.id, m.member_email)}
                disabled={removingId === m.id}
                className="p-2 rounded-lg transition disabled:opacity-50"
                style={{ color: '#7A1F2B' }}
                aria-label={t('team.resend')}
                data-testid={`team-remove-${m.id}-btn`}
              >
                {removingId === m.id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
              </button>
            </div>
          ))}

          {(info.members || []).length === 0 && (
            <div className="text-center py-8 text-sm" style={{ color: '#9CA3AF' }} data-testid="team-no-members-placeholder">
              {t('team.empty')}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
